import React, { useRef } from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface MessageComposerProps extends Omit<BoxProps, 'onChange' | 'children'> {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  /** e.g. "@ MENTION · HOLD PHOTO TO PIN" */
  hintLeft?: React.ReactNode
  /** e.g. "ENTER SENDS" */
  hintRight?: React.ReactNode
  disabled?: boolean
}

const Root = styled(Box)({
  borderTop: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  padding: '14px 20px 18px',
})

const InputBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 9,
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  borderRadius: 15,
  padding: '11px 13px',
  transition: 'border-color var(--ds-duration-fast) var(--ds-ease)',
  '&:focus-within': {
    borderColor: 'rgba(190,157,253,0.5)',
  },
})

const TextArea = styled('textarea')({
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  resize: 'none',
  color: 'var(--ds-night-ink, #fff)',
  fontFamily: 'var(--ds-font)',
  fontSize: 12.5,
  lineHeight: 1.5,
  maxHeight: 84,
  '&::placeholder': {
    color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  },
})

const SendButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'var(--ds-gradient-spectrum, var(--ds-accent))',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  flexShrink: 0,
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
})

const Hint = styled(Box)({
  marginTop: 8,
  display: 'flex',
  justifyContent: 'space-between',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  letterSpacing: '0.08em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
})

/** Auto-growing comment composer — textarea pill + send button + hint row. Enter submits, Shift+Enter for a newline. */
export function MessageComposer({
  value,
  onChange,
  onSubmit,
  placeholder = 'Say something about this moment…',
  hintLeft,
  hintRight,
  disabled,
  ...rest
}: MessageComposerProps) {
  const taRef = useRef<HTMLTextAreaElement>(null)

  const autoGrow = () => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 84)}px`
  }

  return (
    <Root {...rest}>
      <InputBox>
        <TextArea
          ref={taRef}
          rows={1}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value)
            autoGrow()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (value.trim()) onSubmit()
            }
          }}
        />
        <SendButton
          type='button'
          disabled={disabled || !value.trim()}
          onClick={() => value.trim() && onSubmit()}
          aria-label='Send'
        >
          ↑
        </SendButton>
      </InputBox>
      {(hintLeft || hintRight) && (
        <Hint>
          <span>{hintLeft}</span>
          <span>{hintRight}</span>
        </Hint>
      )}
    </Root>
  )
}
